import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import { useDismissible } from "../../native/dismissibleStack";

/** The app's Play listing. The package id is permanent (capacitor.config.ts). */
export const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.cardonomer.app";

const ignoreBack = () => {};

/**
 * Q-31 (owner decision): the API has refused this build as too old
 * (426 APP_UPDATE_REQUIRED, server/middleware/appVersionGate.js), so nothing in
 * the app can work until it is updated. ApiInterceptor renders this the moment
 * any request gets that answer.
 *
 * Deliberately NOT dismissible, like FirstRunConsentGate: there is nothing
 * behind it that works. Escape is disabled, there is no close control, and
 * Android Back is claimed and swallowed so it cannot drop the user into a
 * screen whose every request fails. Above every other overlay, so nothing that
 * opens later can cover it.
 *
 * It does not sign anyone out (useAxios leaves the session alone on this
 * answer): the account is fine, only the build is not.
 *
 * The button is a plain outbound link, as the app's other external links are
 * (Support, Open in Maps): in the packaged app Android hands it to the Play
 * Store.
 */
const UpdateRequiredScreen = () => {
  useDismissible(true, ignoreBack, "app-update-required");

  return (
    <Dialog
      open
      fullScreen
      disableEscapeKeyDown
      aria-labelledby="update-required-title"
      data-testid="update-required-screen"
      sx={{ zIndex: (theme) => theme.zIndex.tooltip + 1 }}
      PaperProps={{
        sx: {
          // Clear the status bar and the gesture bar; at API 36 edge-to-edge is
          // enforced with no opt-out.
          p: 3,
          pb: "calc(24px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))",
          pt: "calc(24px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 2,
          maxWidth: 520,
          mx: "auto",
        },
      }}
    >
      <Typography id="update-required-title" variant="h5" component="h1" sx={{ fontWeight: 900 }}>
        Update required
      </Typography>
      <Typography variant="body1">
        This version of Tradeloft is out of date. Update to the newest version to keep using the app.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Your account, cards and trades are all kept.
      </Typography>
      <Button
        variant="contained"
        size="large"
        component="a"
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ mt: 1 }}
      >
        Update in Google Play
      </Button>
    </Dialog>
  );
};

export default UpdateRequiredScreen;
